/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-array-index-key */
/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TablePagination from '@mui/material/TablePagination';
import DatePicker from 'react-datepicker';
import dateFormat from 'dateformat';
import 'react-datepicker/dist/react-datepicker.css';
import Loader from '../../utils/errorPages/Loader';
import api from '../../services/HttpService';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function EmailLogs() {
  const [paginationData, setPaginationData] = React.useState({
    page: 0,
    pageSize: 10
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [specificDate, setSpecificDate] = useState(null);
  const [dateValue, setDateValue] = React.useState('specific-date');
  const [rowCountState, setRowCountState] = React.useState(0);
  const [groupedData, setGroupedData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [viewOpen, setViewOpen] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState(null);
  const [statistics, setStatistics] = React.useState({
    total_emails: 0,
    sent: 0,
    failed: 0,
    pending: 0
  });

  const groupEmailsByDate = (emails) => {
    const grouped = {};
    emails.forEach((email) => {
      const date = new Date(email.created_at);
      const dateKey = dateFormat(date, 'dddd<br>dd mmm yyyy');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push({
        ...email,
        time: dateFormat(date, 'hh:MM TT')
      });
    });
    return grouped;
  };

  const getInitials = (email) => {
    if (!email) return 'NA';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const fetchEmailLogs = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: paginationData.page + 1,
        per_page: paginationData.pageSize,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(specificDate && { start_date: specificDate, end_date: specificDate }),
        ...(startDate && endDate && { start_date: startDate, end_date: endDate })
      };

      const response = await api.get(`${API_BASE_URL}/api/v1/email-logs`, {
        params
      });

      const emails = response.data.data || [];
      setGroupedData(groupEmailsByDate(emails));
      setRowCountState(response.data.meta?.total || 0);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setGroupedData({});
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/api/v1/email-logs/statistics`);
      setStatistics(response.data.data || {});
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchEmailDetails = async (id) => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/api/v1/email-logs/${id}`, {
        params: { include_body: true, include_headers: true }
      });
      setSelectedEmail(response.data.data);
      setViewOpen(true);
    } catch (error) {
      console.error('Error fetching email details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
    fetchStatistics();
  }, [paginationData, statusFilter, specificDate, startDate, endDate]);

  const handleRangePicker = (date) => {
    if (!date || date.length !== 2 || date[1] === null) return;
    setDateRange(date.map((d) => dateFormat(d, 'yyyy-mm-dd')));
  };

  const handleDatePicker = (date) => {
    if (date) setSpecificDate(dateFormat(new Date(date), 'yyyy-mm-dd'));
  };

  const handleDateChange = (event) => {
    if (event.target.value === 'specific-date') {
      setDateRange([null, null]);
    } else {
      setSpecificDate(null);
    }
    setDateValue(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setPaginationData({ ...paginationData, page: value });
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginationData({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const handleSearch = () => {
    setPaginationData({ page: 0, pageSize: 10 });
    fetchEmailLogs();
  };

  const handleRefresh = () => {
    fetchEmailLogs();
    fetchStatistics();
  };

  const getStatusColor = (status) => {
    if (!status) return '#9e9e9e';
    const colors = { sent: '#4caf50', failed: '#f44336', pending: '#ff9800' };
    return colors[status.toLowerCase()] || '#9e9e9e';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return dateFormat(new Date(dateString), 'dddd, mmmm dd, yyyy');
    } catch {
      return '-';
    }
  };

  return (
    <Box sx={{ mb: 3 }} className="cardWrapper">
      {isLoading && <Loader />}
      <div className="pageTitleWrap">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="h2" variant="h2">
            Email Logs
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mb: 2 }}
          >
            Refresh
          </Button>
        </Stack>
      </div>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={3}>
          <Card sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MailOutlineIcon sx={{ fontSize: 30, color: '#2196f3' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h6">{statistics.total_emails || 0}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon sx={{ fontSize: 30, color: '#4caf50' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Sent
                </Typography>
                <Typography variant="h6">{statistics.sent || 0}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PendingIcon sx={{ fontSize: 30, color: '#ff9800' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Pending
                </Typography>
                <Typography variant="h6">{statistics.pending || 0}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ErrorIcon sx={{ fontSize: 30, color: '#f44336' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h6">{statistics.failed || 0}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={8} xl={8} className="auditTrailWrap">
              <Box>
                {Object.keys(groupedData).length > 0 ? (
                  Object.keys(groupedData).map((date, index) => (
                    <Grid container key={index}>
                      <Grid item xs={2} xl={4}>
                        <Typography
                          sx={{ m: '20px 0 !important' }}
                          variant="h3"
                          component="h3"
                          dangerouslySetInnerHTML={{ __html: date }}
                        />
                      </Grid>
                      <Grid item xs={10} xl={8}>
                        {groupedData[date].map((email, idx) => (
                          <Stack
                            direction="row"
                            key={idx}
                            sx={{ my: 2, alignItems: 'flex-start' }}
                            spacing={2}
                          >
                            <Button
                              sx={{
                                p: '15px !important',
                                minWidth: '80px !important'
                              }}
                              variant="text"
                            >
                              {email.time}
                            </Button>
                            <Card
                              sx={{
                                m: 2,
                                width: '100%',
                                borderLeft: `5px solid ${getStatusColor(email.status)}`,
                                position: 'relative'
                              }}
                            >
                              <CardHeader
                                title={email.subject || 'No Subject'}
                                subheader={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={(email.status || 'unknown').toUpperCase()}
                                      size="small"
                                      sx={{
                                        backgroundColor: getStatusColor(email.status),
                                        color: 'white',
                                        fontSize: '10px',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                    <Typography variant="caption">
                                      To: {email.to?.[0]?.email || 'N/A'}
                                    </Typography>
                                  </Stack>
                                }
                                action={
                                  <Stack direction="row" spacing={2}>
                                    <Tooltip title="View" arrow>
                                      <IconButton
                                        aria-label="settings"
                                        size="small"
                                        onClick={() => fetchEmailDetails(email.id)}
                                      >
                                        <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                }
                              />
                              <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                  From: {email.from?.[0]?.email || 'N/A'}
                                </Typography>
                                {email.attachments && email.attachments.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    <AttachFileIcon
                                      sx={{ fontSize: 14, verticalAlign: 'middle' }}
                                    />{' '}
                                    {email.attachments.length} file(s)
                                  </Typography>
                                )}
                                {email.error_message && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      display: 'block',
                                      mt: 1,
                                      backgroundColor: '#ffebee',
                                      p: 1,
                                      borderRadius: 1
                                    }}
                                  >
                                    {email.error_message}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Stack>
                        ))}
                      </Grid>
                    </Grid>
                  ))
                ) : (
                  <Typography
                    component="h2"
                    variant="h2"
                    sx={{
                      p: 8,
                      textAlign: 'center',
                      opacity: 0.5,
                      fontSize: '26px !important',
                      fontStyle: 'italic',
                      fontWeight: '700'
                    }}
                  >
                    No Email Logs Found
                  </Typography>
                )}
              </Box>
              <Box className="pagination">
                <TablePagination
                  component="div"
                  count={rowCountState}
                  page={paginationData?.page}
                  onPageChange={handlePageChange}
                  rowsPerPage={paginationData?.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </Grid>

            {/* Filters */}
            <Grid item xs={4} xl={4} className="filterWrap">
              <Card sx={{ mt: 2, width: '100%' }}>
                <CardHeader title="Filters" sx={{ py: 2 }} />
                <CardContent sx={{ p: 2 }}>
                  <TextField
                    id="search"
                    fullWidth
                    size="small"
                    label="Search"
                    margin="dense"
                    placeholder="Subject or recipient"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton edge="end" onClick={handleSearch} size="small">
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Autocomplete
                    disablePortal
                    size="small"
                    options={[
                      { label: 'All Status', value: 'all' },
                      { label: 'Sent', value: 'sent' },
                      { label: 'Pending', value: 'pending' },
                      { label: 'Failed', value: 'failed' }
                    ]}
                    sx={{ mt: 1 }}
                    defaultValue={{ label: 'All Status', value: 'all' }}
                    onChange={(e, newvalue) => {
                      setStatusFilter(newvalue?.value || 'all');
                      setPaginationData({ page: 0, pageSize: 10 });
                    }}
                    renderInput={(params) => <TextField {...params} label="Status" />}
                  />

                  <Box sx={{ mt: 2 }}>
                    <FormControl>
                      <FormLabel sx={{ mb: 0.5, fontSize: '14px' }}>Date</FormLabel>
                      <RadioGroup row value={dateValue} onChange={handleDateChange}>
                        <FormControlLabel
                          value="specific-date"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">Specific Date</Typography>}
                        />
                        <FormControlLabel
                          value="specific-range"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">Date Range</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Box className="datepicker" sx={{ mt: 1 }}>
                    {dateValue === 'specific-date' ? (
                      <DatePicker
                        selected={specificDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                          setSpecificDate(date);
                          handleDatePicker(date);
                          setPaginationData({ page: 0, pageSize: 10 });
                        }}
                        isClearable
                        customInput={
                          <TextField
                            label="Date"
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton edge="end" disabled size="small">
                                    <CalendarMonthIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        }
                      />
                    ) : (
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={new Date()}
                        onChange={(update) => {
                          setDateRange(update);
                          handleRangePicker(update);
                          setPaginationData({ page: 0, pageSize: 10 });
                        }}
                        monthsShown={2}
                        isClearable
                        customInput={
                          <TextField
                            label="Date Range"
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton edge="end" disabled size="small">
                                    <CalendarMonthIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        }
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Email Detail Dialog - Compact 2-Column Layout */}
      {viewOpen && selectedEmail && (
        <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Email Log View</DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {/* Header with Avatar */}
            <Box sx={{ display: 'flex', mb: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: '#1a3a52',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  mr: 3
                }}
              >
                {getInitials(selectedEmail.from?.[0]?.email)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  {selectedEmail.from?.[0]?.name || selectedEmail.from?.[0]?.email || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedEmail.from?.[0]?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  IP : {selectedEmail.ip_address || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Stack spacing={1}>
                  {/* Created At */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Date:
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(selectedEmail.created_at)}
                    </Typography>
                  </Box>

                  {/* Event */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Event:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedEmail.subject}
                    </Typography>
                  </Box>

                  {/* Status */}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip
                      label={(selectedEmail.status || 'UNKNOWN').toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(selectedEmail.status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>

                  {/* Sent At */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Sent At:
                    </Typography>
                    <Typography variant="body2">
                      {selectedEmail.sent_at
                        ? dateFormat(new Date(selectedEmail.sent_at), 'mmm dd, yyyy HH:MM:ss')
                        : 'Not sent yet'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {/* Email Details - Compact 2-Column Grid */}
            <Grid container spacing={2}>
              {/* Subject - Full Width */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {selectedEmail.subject}
                </Typography>
              </Grid>

              {/* To - Half Width */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  To
                </Typography>
                <Typography variant="body2">
                  {(selectedEmail.to || []).map((r, i) => (
                    <span key={i}>
                      {r.email}
                      {i < selectedEmail.to.length - 1 && ', '}
                    </span>
                  ))}
                </Typography>
              </Grid>

              {/* CC - Half Width */}
              {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    CC
                  </Typography>
                  <Typography variant="body2">
                    {selectedEmail.cc.map((r, i) => (
                      <span key={i}>
                        {r.email}
                        {i < selectedEmail.cc.length - 1 && ', '}
                      </span>
                    ))}
                  </Typography>
                </Grid>
              )}

              {/* Reply To */}
              {selectedEmail.reply_to && selectedEmail.reply_to.length > 0 && (
                <Grid item xs={selectedEmail.cc && selectedEmail.cc.length > 0 ? 12 : 6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reply To
                  </Typography>
                  <Typography variant="body2">
                    {selectedEmail.reply_to.map((r) => r.email).join(', ')}
                  </Typography>
                </Grid>
              )}

              {/* Mailer - Half Width */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mailer
                </Typography>
                <Typography variant="body2">{selectedEmail.mailer}</Typography>
              </Grid>

              {/* Attachments - Full Width */}
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Attachments
                  </Typography>

                  <Box>
                    {selectedEmail.attachments.map((att, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.5
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />

                        {/* Clickable file */}
                        <Typography
                          variant="body2"
                          component="a"
                          href={att.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: 'none',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {att.filename}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({(att.size / 1024).toFixed(1)} KB)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Error Message - Full Width */}
              {selectedEmail.error_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="error">
                    Error Message
                  </Typography>
                  <Box sx={{ bgcolor: '#ffebee', p: 1.5, borderRadius: 1, mt: 0.5 }}>
                    <Typography variant="body2" color="error">
                      {selectedEmail.error_message}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Email Body - Full Width */}
              {selectedEmail.body_html && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email Body
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: '400px',
                      overflow: 'auto',
                      bgcolor: '#fafafa'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewOpen(false)} variant="contained" sx={{ minWidth: 120 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
